/**
 * Data transformation utilities for normalizing VideoData and telemetry
 * into consistent session objects regardless of data source
 */

/**
 * Normalize VideoData record into consistent session object
 * @param {Object} videoData - Raw VideoData from database
 * @returns {Object} Normalized session object
 */
export function normalizeVideoData(videoData) {
  if (!videoData) {
    throw new Error('VideoData is required');
  }

  // Determine available video URLs (priority order)
  const videoUrl = getVideoUrl(videoData);

  // Get crossStart time first (needed for lap parsing)
  const crossStart = videoData.crossStart || 0;

  // Parse lap data if available (pass crossStart for time calculations)
  const lapData = parseLapData(videoData.laptimeData, crossStart);

  // Determine capabilities
  const capabilities = {
    hasLapData: lapData && lapData.length > 0,
    hasTelemetry: !!(
      videoData.telemetryS3Key ||
      videoData.tempLocalTelemetryLink ||
      videoData.tracksideTelemetryUrl
    ),
    hasSegments: false, // Will be determined after telemetry load
  };

  return {
    id: videoData.id,
    videoUrl,
    crossStart,
    capabilities,
    lapData: lapData || [],
    telemetry: null, // Will be populated by useVideoSession hook
    segments: [], // Will be populated based on track/telemetry
    metadata: {
      trackName: extractTrackName(videoData),
      carNum: videoData.carNum,
      drivername: videoData.drivername || videoData.username,
      cameraAngle: videoData.cameraAngle,
      eventId: videoData.eventId,
      sessionId: videoData.sessionId,
      createdAt: videoData.createdAt,
    },
    raw: videoData, // Keep reference to original data
  };
}

/**
 * Get the best available video URL from VideoData
 * @param {Object} videoData - VideoData record
 * @returns {string|null} Video URL or null if none available
 */
function getVideoUrl(videoData) {
  // Priority order: streamUrl -> youtubeStreamUrl -> tracksideVideoUrl -> tempLocalVideoLink
  return (
    videoData.streamUrl ||
    videoData.youtubeStreamUrl ||
    videoData.tracksideVideoUrl ||
    videoData.tempLocalVideoLink ||
    null
  );
}

/**
 * Parse lap data from various sources
 * @param {string|Object|null} laptimeData - Raw lap data (JSON string or object)
 * @param {number} crossStart - Cross start time for calculating lap start times
 * @returns {Array} Normalized lap data array
 */
function parseLapData(laptimeData, crossStart = 0) {
  console.log('🔍 parseLapData received:', {
    laptimeData,
    type: typeof laptimeData,
    isString: typeof laptimeData === 'string',
    isNull: laptimeData === null,
    isUndefined: laptimeData === undefined,
  });

  if (!laptimeData) {
    console.log('❌ No laptimeData provided');
    return [];
  }

  try {
    let parsed = laptimeData;
    if (typeof laptimeData === 'string') {
      console.log('📄 Parsing JSON string...');
      parsed = JSON.parse(laptimeData);
    }

    console.log('📊 Parsed data:', parsed);

    // Handle different lap data formats
    if (Array.isArray(parsed)) {
      console.log('✅ Found array format, normalizing...');
      const normalized = normalizeLapArray(parsed, crossStart);
      console.log('🏁 Normalized lap data:', normalized);
      return normalized;
    }

    // Handle object with laps property
    if (parsed.laps && Array.isArray(parsed.laps)) {
      console.log('✅ Found object with laps array, normalizing...');
      const normalized = normalizeLapArray(parsed.laps, crossStart);
      console.log('🏁 Normalized lap data:', normalized);
      return normalized;
    }

    // Handle CSV-imported data format (data property)
    if (parsed.data && Array.isArray(parsed.data)) {
      console.log('✅ Found CSV data format, normalizing...');
      const normalized = normalizeLapArray(parsed.data, crossStart);
      console.log('🏁 Normalized lap data:', normalized);
      return normalized;
    }

    console.log('⚠️ Unrecognized lap data format');
    return [];
  } catch (error) {
    console.warn('❌ Failed to parse lap data:', error);
    return [];
  }
}

/**
 * Normalize lap array to consistent format and calculate start times
 * @param {Array} laps - Raw lap array
 * @param {number} crossStart - Video cross start time in seconds
 * @returns {Array} Normalized lap array with calculated start times
 */
function normalizeLapArray(laps, crossStart = 0) {
  // Parse lap time string (e.g., "1:23.456") to seconds
  const parseLapTime = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') return 0;

    // Handle formats like "1:23.456" or "83.456"
    const parts = timeStr.split(':');
    if (parts.length === 2) {
      // Format: "1:23.456"
      const minutes = parseInt(parts[0]) || 0;
      const seconds = parseFloat(parts[1]) || 0;
      return minutes * 60 + seconds;
    } else {
      // Format: "83.456" (just seconds)
      return parseFloat(timeStr) || 0;
    }
  };

  // First pass: normalize basic data
  const normalizedLaps = laps.map((lap, index) => ({
    // Standard fields
    number: lap.number ?? lap.lapNumber ?? parseInt(lap.Lap) ?? index + 1,
    startTime: lap.startTime ?? lap.adjTimeCummLapStart ?? 0, // Will be calculated below
    duration: lap.duration ?? lap.time ?? parseLapTime(lap['Lap Time']) ?? 0,
    position: lap.position ?? lap.Pos ?? parseInt(lap.Pos) ?? 1,

    // Additional fields from CSV format
    speed: lap.Speed ? parseFloat(lap.Speed.replace(' mi/h', '')) : null,
    diffToBest: lap['Diff to Best Lap']
      ? parseFloat(lap['Diff to Best Lap'])
      : null,
    diffToLast: lap['Diff to Last Lap']
      ? parseFloat(lap['Diff to Last Lap'])
      : null,
    gapInFront: lap['Gap in Front'] ? parseFloat(lap['Gap in Front']) : null,
    diffToP1: lap['Diff to P1'] ? parseFloat(lap['Diff to P1']) : null,

    // Keep original data for reference
    ...lap,
  }));

  // Second pass: calculate cumulative start times
  let cumulativeTime = crossStart;

  const processedLaps = normalizedLaps.map((lap, index) => {
    const startTime = cumulativeTime;
    cumulativeTime += lap.duration;

    return {
      ...lap,
      startTime,
      // Also add end time for convenience
      endTime: cumulativeTime,
    };
  });

  // Add synthetic Out Lap if there's significant time before first lap
  if (processedLaps.length > 0 && crossStart > 5) {
    const outLap = {
      number: 0,
      startTime: 0, // From video start
      duration: crossStart, // Duration until first real lap starts
      endTime: crossStart,
      position: processedLaps[0]?.position || 1,
      synthetic: true, // Mark as synthetic
      name: 'Out Lap',
    };

    return [outLap, ...processedLaps];
  }

  return processedLaps;
}

/**
 * Extract track name from VideoData
 * @param {Object} videoData - VideoData record
 * @returns {string} Track name or 'Unknown Track'
 */
function extractTrackName(videoData) {
  // Try various sources for track name
  if (videoData.eventVenue) return videoData.eventVenue;
  if (videoData.trackName) return videoData.trackName;
  if (videoData.eventId) return `Event ${videoData.eventId}`;
  return 'Unknown Track';
}

/**
 * Normalize telemetry data from S3 file
 * @param {Object} telemetryData - Raw telemetry data from JSON file
 * @returns {Object} Normalized telemetry object
 */
export function normalizeTelemetryData(telemetryData) {
  if (!telemetryData) return null;

  try {
    const {
      telemetry_system,
      car,
      session,
      user,
      laps,
      locations,
      trackLayout,
    } = telemetryData;

    // Normalize locations data
    const normalizedLocations =
      locations?.map((location) => ({
        timestamp: location.timestamp,
        sessionElapsedTime: location.sessionElapsedTime,
        lapNumber: location.lapNumber,
        latitude: location.latitude,
        longitude: location.longitude,
        speed: location.speed,
        bearing: location.bearing,
        accuracy: location.accuracy,
        // Sensor data
        accelerometerX: location.accelerometerX,
        accelerometerY: location.accelerometerY,
        accelerometerZ: location.accelerometerZ,
        gyroscopeX: location.gyroscopeX,
        gyroscopeY: location.gyroscopeY,
        gyroscopeZ: location.gyroscopeZ,
        // Distance and segment data (pre-computed or calculated)
        distance: location.distance || null,
        cumulativeDistance: location.cumulativeDistance || null,
        segmentNumber: location.segmentNumber || null,
        segmentDistance: location.segmentDistance || null,
      })) || [];

    // Normalize laps from telemetry
    const normalizedLaps =
      laps?.map((lap) => ({
        number: lap.lapNumber,
        startTime: convertTimestampToVideoTime(
          lap.startTime,
          session?.startDateTime
        ),
        endTime: convertTimestampToVideoTime(
          lap.endTime,
          session?.startDateTime
        ),
        duration: lap.duration / 1000, // Convert ms to seconds
        sessionStartTime: lap.startTime,
        sessionEndTime: lap.endTime,
        distance: lap.distance || null,
      })) || [];

    // Normalize track segments if available
    const normalizedSegments =
      trackLayout?.segments?.map((segment) => ({
        number: segment.number,
        name: segment.name,
        startDistance: segment.startDistance,
        endDistance: segment.endDistance,
        length: segment.endDistance - segment.startDistance,
      })) || null;

    return {
      system: telemetry_system,
      car,
      session,
      user,
      laps: normalizedLaps,
      locations: normalizedLocations,
      segments: normalizedSegments,
      // Computed fields
      totalDuration: session?.endDateTime - session?.startDateTime,
      trackName: session?.trackName || 'Unknown Track',
      configName: session?.trackConfigName,
      totalDistance: trackLayout?.totalDistance || null,
    };
  } catch (error) {
    console.error('Failed to normalize telemetry data:', error);
    return null;
  }
}

/**
 * Convert absolute timestamp to video time (seconds from video start)
 * @param {number} timestamp - Absolute timestamp
 * @param {number} sessionStartTime - Session start timestamp
 * @returns {number} Video time in seconds
 */
function convertTimestampToVideoTime(timestamp, sessionStartTime) {
  if (!timestamp || !sessionStartTime) return 0;
  return (timestamp - sessionStartTime) / 1000; // Convert ms to seconds
}

/**
 * Merge lap data from multiple sources (VideoData laptime + telemetry laps)
 * @param {Array} videoLapData - Lap data from VideoData.laptimeData
 * @param {Array} telemetryLapData - Lap data from telemetry file
 * @param {number} crossStart - Cross start time for video sync
 * @returns {Array} Merged and normalized lap data
 */
export function mergeLapData(
  videoLapData = [],
  telemetryLapData = [],
  crossStart = 0
) {
  // If we have telemetry lap data, prefer it as it's more accurate
  if (telemetryLapData && telemetryLapData.length > 0) {
    return telemetryLapData.map((lap) => ({
      ...lap,
      // Adjust start time for video synchronization
      startTime: lap.startTime + crossStart,
    }));
  }

  // Fall back to VideoData lap data
  if (videoLapData && videoLapData.length > 0) {
    return videoLapData;
  }

  // For video-only mode, return empty array - don't create synthetic laps
  // This preserves the correct capability detection for hasLapData
  return [];
}

/**
 * Create capabilities object based on available data
 * @param {Object} sessionData - Normalized session data
 * @returns {Object} Capabilities object
 */
export function calculateCapabilities(sessionData) {
  const hasLapData = sessionData.lapData && sessionData.lapData.length > 0;
  const hasTelemetry =
    sessionData.telemetry &&
    sessionData.telemetry.locations &&
    sessionData.telemetry.locations.length > 0;
  const hasSegments =
    (sessionData.telemetry &&
      sessionData.telemetry.segments &&
      sessionData.telemetry.segments.length > 0) ||
    (sessionData.segments && sessionData.segments.length > 0);
  const hasDistance =
    hasTelemetry &&
    sessionData.telemetry.locations.some(
      (loc) => loc.distance !== null || loc.cumulativeDistance !== null
    );

  return {
    hasVideo: !!sessionData.videoUrl,
    hasLapData,
    hasTelemetry,
    hasSegments,
    hasDistance,
    // Computed capabilities
    hasNavigation: hasLapData,
    hasCharts: hasTelemetry,
    hasSync: hasTelemetry || hasLapData,
    hasDistanceNavigation: hasDistance && hasSegments,
    // Statistics
    lapCount: hasLapData ? sessionData.lapData.length : 0,
    telemetryPoints: hasTelemetry ? sessionData.telemetry.locations.length : 0,
    segmentCount: hasSegments
      ? sessionData.telemetry?.segments?.length ||
        sessionData.segments?.length ||
        0
      : 0,
    // Track info
    trackInfo: {
      name:
        sessionData.telemetry?.trackName ||
        sessionData.metadata?.trackName ||
        'Unknown Track',
      config: sessionData.telemetry?.configName || null,
      totalDistance: sessionData.telemetry?.totalDistance || null,
    },
  };
}
